
import React, { useState } from 'react';
import './CartPage.css';
import { useApp } from '../../context/AppContext';

const CartPage: React.FC = () => {
  const { cart, products, cartTotal, updateCartQuantity, removeFromCart, clearCart, currentUser, placeOrder, setActiveTab } = useApp();
  const [showCheckout, setShowCheckout] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(currentUser.shippingAddresses[0]?.id || '');
  const [processing, setProcessing] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);

  const shipping = cart.length > 0 ? 9.99 : 0;
  const tax = cartTotal * 0.08;
  const total = cartTotal + shipping + tax;

  const cartItems = cart.map(item => {
    const product = products.find(p => p.id === item.productId)!;
    return { ...item, product };
  });

  const handleCheckout = () => {
    if (!selectedAddress) return;
    setProcessing(true);
    setTimeout(() => {
      placeOrder(selectedAddress);
      setOrderComplete(true);
      setProcessing(false);
    }, 2000);
  };

  if (orderComplete) {
    return (
      <div className="cart-page">
        <div className="order-success animate-scale-in">
          <div className="success-icon">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
            </svg>
          </div>
          <h2>Order Placed!</h2>
          <p>Thank you for your purchase. You'll receive a confirmation email shortly.</p>
          <button className="continue-btn" onClick={() => { setOrderComplete(false); setShowCheckout(false); setActiveTab('shop'); }}>
            Continue Shopping
          </button>
        </div>
      </div>
    );
  }

  if (cart.length === 0) {
    return (
      <div className="cart-page">
        <div className="empty-cart">
          <div className="empty-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="9" cy="21" r="1"/>
              <circle cx="20" cy="21" r="1"/>
              <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/>
            </svg>
          </div>
          <h2>Your cart is empty</h2>
          <p>Discover amazing products in our shop!</p>
          <button className="shop-now-btn" onClick={() => setActiveTab('shop')}>
            Shop Now
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-page">
      {!showCheckout ? (
        <>
          <div className="cart-items">
            {cartItems.map(item => (
              <div key={`${item.productId}-${item.size}`} className="cart-item animate-fade-in">
                <img src={item.product.images[0]} alt={item.product.name} className="item-image" />
                <div className="item-details">
                  <h3>{item.product.name}</h3>
                  <p className="item-size">Size: {item.size}</p>
                  <p className="item-price">${(item.product.salePrice || item.product.price).toFixed(2)}</p>
                </div>
                <div className="item-actions">
                  <div className="quantity-control">
                    <button onClick={() => updateCartQuantity(item.productId, item.size, item.quantity - 1)}>-</button>
                    <span>{item.quantity}</span>
                    <button onClick={() => updateCartQuantity(item.productId, item.size, item.quantity + 1)}>+</button>
                  </div>
                  <button className="remove-btn" onClick={() => removeFromCart(item.productId, item.size)}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="cart-summary">
            <div className="summary-row">
              <span>Subtotal</span>
              <span>${cartTotal.toFixed(2)}</span>
            </div>
            <div className="summary-row">
              <span>Shipping</span>
              <span>${shipping.toFixed(2)}</span>
            </div>
            <div className="summary-row">
              <span>Tax</span>
              <span>${tax.toFixed(2)}</span>
            </div>
            <div className="summary-row total">
              <span>Total</span>
              <span>${total.toFixed(2)}</span>
            </div>
            <button className="checkout-btn" onClick={() => setShowCheckout(true)}>
              Proceed to Checkout
            </button>
            <button className="clear-btn" onClick={clearCart}>Clear Cart</button>
          </div>
        </>
      ) : (
        <div className="checkout-section animate-fade-in">
          <h2>Checkout</h2>
          
          <div className="checkout-field">
            <h3>Shipping Address</h3>
            {currentUser.shippingAddresses.length > 0 ? (
              <div className="address-options">
                {currentUser.shippingAddresses.map(addr => (
                  <label key={addr.id} className={`address-option ${selectedAddress === addr.id ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="address"
                      value={addr.id}
                      checked={selectedAddress === addr.id}
                      onChange={() => setSelectedAddress(addr.id)}
                    />
                    <div className="address-content">
                      <strong>{addr.name}</strong>
                      <p>{addr.street}</p>
                      <p>{addr.city}, {addr.state} {addr.zip}</p>
                    </div>
                  </label>
                ))}
              </div>
            ) : (
              <p className="no-address">Please add a shipping address in your profile</p>
            )}
          </div>

          <div className="order-summary">
            <h3>Order Summary</h3>
            {cartItems.map(item => (
              <div key={`${item.productId}-${item.size}`} className="summary-item">
                <span>{item.product.name} × {item.quantity}</span>
                <span>${((item.product.salePrice || item.product.price) * item.quantity).toFixed(2)}</span>
              </div>
            ))}
            <div className="summary-divider" />
            <div className="summary-item">
              <span>Subtotal</span>
              <span>${cartTotal.toFixed(2)}</span>
            </div>
            <div className="summary-item">
              <span>Shipping</span>
              <span>${shipping.toFixed(2)}</span>
            </div>
            <div className="summary-item">
              <span>Tax</span>
              <span>${tax.toFixed(2)}</span>
            </div>
            <div className="summary-item total">
              <span>Total</span>
              <span>${total.toFixed(2)}</span>
            </div>
          </div>

          <button 
            className={`pay-btn ${processing ? 'processing' : ''}`}
            onClick={handleCheckout}
            disabled={!selectedAddress || processing}
          >
            {processing ? (
              <span className="spinner" />
            ) : (
              <>
                <svg viewBox="0 0 24 24" fill="currentColor" className="paypal-icon">
                  <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 2.16A.762.762 0 0 1 5.697 1.5h6.49c2.062 0 3.624.534 4.645 1.588.963.998 1.363 2.392 1.188 4.145l-.003.028c-.253 1.612-.916 2.886-1.97 3.787-1.046.895-2.393 1.35-4.003 1.35H9.863l-.698 4.447a.637.637 0 0 1-.63.536H7.076z"/>
                </svg>
                Pay ${total.toFixed(2)} with PayPal
              </>
            )}
          </button>

          <button className="back-btn" onClick={() => setShowCheckout(false)}>
            ← Back to Cart
          </button>
        </div>
      )}
    </div>
  );
};

export default CartPage;
